import { COMMONS } from "../calendar/commons";
import {
    Celebration,
    getMorningAndEveningCelebrations,
} from "../calendar/ordinariate-calendar";
import { CalendarDay } from "../calendar/utils";

import { LoadableDataDependency } from "../utils/RemoteDataProvider";

export const useCalcheck = (
    loadableTextList: LoadableDataDependency<string[]>
) => {
    const getEasterId = (
        day: CalendarDay,
        id: string,
        checkEaster: boolean
    ) => {
        const isEastertide = day.easterOctave;

        if (isEastertide && checkEaster) {
            return `${id} [easter]`;
        } else {
            return id;
        }
    };

    const loadableTextIsMissing = (
        day: CalendarDay,
        celebration: Celebration,
        key: string,
        checkEaster: boolean
    ) => {
        let missing = true;

        if (loadableTextList.state === "loaded") {
            const map: any = celebration;

            if (
                loadableTextList.value.includes(
                    getEasterId(day, map[key] ?? "", checkEaster)
                )
            ) {
                missing = false;
            }

            if (celebration.commons && missing) {
                missing = false;

                celebration.commons.forEach((common) => {
                    let map: any = COMMONS[common] ?? {};

                    if (day.easterOctave && map.easterOverrides) {
                        map = { ...map, ...map.easterOverrides };
                    }

                    if (
                        !loadableTextList.value.includes(
                            getEasterId(day, map[key] ?? "", checkEaster)
                        )
                    ) {
                        missing = true;
                    }
                });
            }
        }

        return missing;
    };

    const valueIsMissing = (
        day: CalendarDay,
        celebration: Celebration,
        key: string
    ) => {
        let missing = true;

        if (loadableTextList.state === "loaded") {
            const map: any = celebration;

            if (map[key]) {
                missing = false;
            }

            if (celebration.commons && missing) {
                missing = false;

                celebration.commons.forEach((common) => {
                    let map: any = COMMONS[common] ?? {};

                    if (day.easterOctave && map.easterOverrides) {
                        map = { ...map, ...map.easterOverrides };
                    }

                    if (!map[key]) {
                        missing = true;
                    }
                });
            }
        }

        return missing;
    };

    const getCelebrationIssues = (
        day: CalendarDay,
        celebration: Celebration,
        hour: "first-evensong" | "mattins" | "second-evensong"
    ) => {
        const issues: string[] = [];

        if (celebration.propersOfTheFeria) {
            return issues;
        }

        if (!celebration.collect || celebration.collect.includes("TODO")) {
            issues.push("No collect");
        }

        if (
            hour === "first-evensong" &&
            loadableTextIsMissing(day, celebration, "ep1HymnId", false)
        ) {
            issues.push("No EP1 Hymn");
        }

        if (
            hour === "first-evensong" &&
            valueIsMissing(day, celebration, "ep1VandR")
        ) {
            issues.push("No EP1 V&R");
        }

        if (
            hour === "first-evensong" &&
            loadableTextIsMissing(day, celebration, "ep1MagId", true)
        ) {
            issues.push("No EP1 Mag");
        }

        if (
            hour === "mattins" &&
            loadableTextIsMissing(day, celebration, "invId", true)
        ) {
            issues.push("No MP Inv");
        }

        if (
            hour === "mattins" &&
            loadableTextIsMissing(day, celebration, "mpHymnId", false)
        ) {
            issues.push("No MP Hymn");
        }

        if (hour === "mattins" && valueIsMissing(day, celebration, "mpVandR")) {
            issues.push("No MP V&R");
        }

        if (
            hour === "mattins" &&
            loadableTextIsMissing(day, celebration, "mpBenId", true)
        ) {
            issues.push("No MP Ben");
        }

        if (
            hour === "second-evensong" &&
            loadableTextIsMissing(day, celebration, "ep2HymnId", false)
        ) {
            issues.push("No EP2 Hymn");
        }

        if (
            hour === "second-evensong" &&
            valueIsMissing(day, celebration, "ep2VandR")
        ) {
            issues.push("No EP2 V&R");
        }

        if (
            hour === "second-evensong" &&
            loadableTextIsMissing(day, celebration, "ep2MagId", true)
        ) {
            issues.push("No EP2 Mag");
        }

        return issues;
    };

    const getDaySectionIssues = (
        day: CalendarDay,
        celebrations: Celebration[],
        outrankedCelebrations: Celebration[],
        isFirstEvensong: string[],
        isMattins: boolean
    ) => {
        const celebrationIssues: {
            day: CalendarDay;
            celebration: Celebration;
            hour: string;
            issues: string[];
            outranked: boolean;
        }[] = [];

        celebrations.forEach((celebration) => {
            const hour = isMattins
                ? "mattins"
                : isFirstEvensong.find((ife) => ife === celebration.label)
                ? "first-evensong"
                : "second-evensong";

            const issues = getCelebrationIssues(day, celebration, hour);

            if (issues.length > 0) {
                celebrationIssues.push({
                    day,
                    celebration,
                    hour,
                    issues,
                    outranked: false,
                });
            }
        });

        outrankedCelebrations.forEach((celebration) => {
            const hour = isMattins
                ? "mattins"
                : isFirstEvensong.find((ife) => ife === celebration.label)
                ? "first-evensong"
                : "second-evensong";

            const issues = getCelebrationIssues(day, celebration, hour);

            if (issues.length > 0) {
                const issue = {
                    day,
                    celebration,
                    hour,
                    issues,
                    outranked: true,
                };

                celebrationIssues.push(issue);
            }
        });

        return celebrationIssues;
    };

    const getNextDayWithIssues = () => {
        const year = new Date().getFullYear();
        const regionLabels = localStorage
            .getItem("region-switches")
            ?.split(";;;");

        const days = getMorningAndEveningCelebrations(
            year,
            false,
            false,
            false,
            regionLabels
        ).concat(
            getMorningAndEveningCelebrations(
                year + 1,
                false,
                false,
                false,
                regionLabels
            )
        );

        const todayIndex = days.findIndex(
            (day) =>
                day.date === new Date().getDate() &&
                day.month === new Date().getMonth() + 1 &&
                day.year === new Date().getFullYear()
        );

        let daysInFuture = 0;

        for (const day of days.slice(todayIndex)) {
            const calendarDay: CalendarDay = {
                year,
                month: day.month,
                date: day.date,
                dayOfTheWeek: "mon",
                label: "",
                collect: "",
                collectId: "",
                otherLabels: [],
                dayOfTheYear: 0,
                weekOfTheYear: 0,
                antiphonId: "",
                sentenceId: "",
                hymnId: "",
                // any eastertide day
                easterOctave:
                    day.morningOptions
                        .concat(day.morningOutranked)
                        .find(
                            (option) =>
                                option.title.includes("Easter") ||
                                option.title.includes("Ascension") ||
                                option.title.includes("Whit") ||
                                option.title.includes("Pentecost")
                        ) !== undefined,
            };

            const amIssues = getDaySectionIssues(
                calendarDay,
                day.morningOptions,
                day.morningOutranked,
                [],
                true
            );

            const pmIssues = getDaySectionIssues(
                calendarDay,
                day.eveningOptions,
                day.eveningOutranked,
                day.isFirstEvensong,
                false
            );

            if (amIssues.length > 0 || pmIssues.length > 0) {
                return { daysInFuture, amIssues, pmIssues };
            }

            daysInFuture++;
        }
    };

    return { loadableTextIsMissing, valueIsMissing, getNextDayWithIssues };
};
